'use client';

import clsx from 'clsx';
import { FC, useState } from 'react';
import { useProductDetailBreadcrumb } from 'atoms/Breadcrumbs/hooks';
import { Button } from 'atoms/Button';
import { Link } from 'atoms/Link/Link';
import { ParseBBCode } from 'atoms/ParseBBCode';
import { H2, P, Span } from 'atoms/Typography';
import { VariantItem } from 'atoms/VariantLink';
import { OldPrice } from 'molecules/OldPrice';
import { ProductImage } from 'molecules/ProductImage';
import { ProductReferencePrice } from 'molecules/ProductReferencePrice';
import { ProductSize } from 'molecules/ProductSize';
import { ReviewsRating } from 'molecules/ReviewsRating';
import { Vat } from 'molecules/Vat';
import {
  PriceInfo,
  ProductQuantitySelect,
} from 'organisms/ProductPriceSection';
import { ProductPrice } from 'organisms/ProductPriceSection/PriceInfo';
import { SizeSelectBare } from 'organisms/ProductPriceSection/SizeSelect';
import { ProductMaterialInfo } from 'pages/ProductDetail/ProductMaterialInfo';
import { ProductSizesProvider } from 'pages/ProductDetail/context';
import { useProductSizeSelect } from 'pages/ProductDetail/hooks';
import { RichTextMarkdown } from 'pages/StoryBlok/RichText';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useMediaQuery } from 'modules/mui/hooks';
import { useCart } from 'modules/shop/cart/hooks';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import { IAdaptedBasicProduct } from 'commerce/scayle/utils/adapter/types';
import { IProductDetailsTile, ISelectedProduct } from '../types';
import styles from './styles.module.css';

export const ProductDetailsTile: FC<IProductDetailsTile> = ({ product }) => {
  const { referenceKey, productDescription, isSaleable } = product;
  const t = useTranslations();
  const { isDesktop } = useMediaQuery();
  const { gtmData } = useSiteConfig();
  const { addToCart } = useCart();

  const [state, setState] = useState({
    quantity: 1,
    productRefKey: product.referenceKey,
  });

  const siblings = product.siblings || [];
  const productWithSiblingsByRefKey: Record<string, IAdaptedBasicProduct> = {
    [referenceKey]: product,
  };
  siblings.forEach(
    (sibling) => (productWithSiblingsByRefKey[sibling.referenceKey] = sibling),
  );

  const selectedProduct = productWithSiblingsByRefKey[
    state.productRefKey
  ] as ISelectedProduct;

  const { category, brand, brandLink, size: productSize } = selectedProduct;

  const categoryBreadcrumb = useProductDetailBreadcrumb(product.masterCategory);
  const currentCategory = categoryBreadcrumb.current?.href ?? '';

  const {
    hasSizes,
    sizeOptions,
    selectedVariant,
    quantityLimit,
    setSelectedVariant,
  } = useProductSizeSelect(selectedProduct, selectedProduct.referenceKey, true);

  const activeVariant =
    selectedProduct.variants?.find(
      (variant) => variant.referenceKey === selectedVariant,
    ) || selectedProduct.variants?.[0];

  const isOutOfStock =
    !activeVariant?.stock || activeVariant.stock.quantity === 0;
  const isDisabled = isOutOfStock || (hasSizes && !selectedVariant);

  const filteredVariants = Object.values(productWithSiblingsByRefKey).filter(
    (variant) => !variant.isSoldOut,
  );

  const transformedSizeOptions = sizeOptions.map((option) => ({
    ...option,
    label:
      option.disabled && option.value
        ? t('product.pdp.soldOut', { label: option.label })
        : option.label,
  }));

  const isDescriptionVisible =
    productDescription && ((isSaleable && isDesktop) || !isSaleable);

  const handleClick = async () => {
    if (!activeVariant?.referenceKey) {
      return;
    }

    addToCart(
      activeVariant.referenceKey,
      state.quantity,
      gtmData?.pageType,
      selectedProduct.limitForCart,
    );
  };

  return (
    <ProductSizesProvider product={selectedProduct}>
      <div className={styles.tile}>
        <ProductImage
          hash={selectedProduct.coverImage}
          alt={selectedProduct.name}
          className={styles.img}
        />
        <div className={styles.productInfo}>
          <div className={styles.topInfo}>
            {!isSaleable && (
              <div>
                {/** @todo add correct href to Link */}
                {category && (
                  <Link href={currentCategory} className={styles.category}>
                    {category}
                  </Link>
                )}
                {category && brand && <Span> - </Span>}
                {brand &&
                  (brandLink ? (
                    <Link href={brandLink} className={styles.category}>
                      {brand}
                    </Link>
                  ) : (
                    <Span>{brand}</Span>
                  ))}
              </div>
            )}
            <H2 className={styles.h2}>
              <ParseBBCode content={selectedProduct.productName} />
            </H2>
            <div className={styles.metaInfo}>
              <ProductMaterialInfo
                size={productSize}
                composedMaterials={selectedProduct.composedMaterials}
              />
              {productSize && <ProductSize size={productSize} />}
              {activeVariant?.price && (
                <ProductReferencePrice price={activeVariant.price} />
              )}
            </div>
            {!isSaleable && (
              <ReviewsRating
                url={selectedProduct.href}
                value={product?.ratingScore}
                count={product?.ratingCount}
                size="small"
              />
            )}
          </div>

          {isDescriptionVisible && (
            <RichTextMarkdown
              text={productDescription}
              components={{
                p: ({ children }) => <P className={styles.p}>{children}</P>,
              }}
            />
          )}

          {isSaleable && (
            <>
              <PriceInfo className={styles.prices}>
                <ProductPrice
                  activeVariant={activeVariant}
                  className={styles.currentPrice}
                  currencyClassName={styles.currency}
                />
                <OldPrice
                  amount={selectedProduct.price.recommendedRetailPrice}
                  currencyCode={selectedProduct.price.currencyCode}
                  className={styles.oldPrice}
                  currencyClassName={styles.currency}
                />
              </PriceInfo>
              <Vat
                rate={selectedProduct.taxRatePercent}
                className={styles.vats}
              />
              <div className={styles.colors}>
                {filteredVariants.map((variant) => (
                  <VariantItem
                    key={variant.id}
                    colors={variant.colors}
                    isActive={
                      variant.id.toString() === selectedProduct?.id?.toString()
                    }
                    onClick={() =>
                      setState((s) => ({
                        ...s,
                        productRefKey: variant.referenceKey,
                      }))
                    }
                    className={clsx(
                      styles.variantItem,
                      variant.isSoldOut && styles.isSoldOut,
                    )}
                  />
                ))}
              </div>
              {hasSizes && (
                <SizeSelectBare
                  options={transformedSizeOptions}
                  classes={{ root: styles.productDetailSelect }}
                  value={selectedVariant}
                  onChange={(event) => setSelectedVariant(event.target.value)}
                />
              )}
              {!!selectedVariant && (
                <ProductQuantitySelect
                  setQuantity={(quantity) =>
                    setState((s) => ({ ...s, quantity }))
                  }
                  quantityLimit={quantityLimit}
                  isOutOfStock={isDisabled}
                  className={styles.productDetailSelect}
                />
              )}
              <Button
                className={styles.button}
                theme="dark"
                onClick={handleClick}
                disabled={isDisabled}
              >
                {t('common.buttons.buy')}
              </Button>
            </>
          )}
        </div>
      </div>
    </ProductSizesProvider>
  );
};
